import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import EditIcon from '@mui/icons-material/EditRounded';
import DeleteIcon from '@mui/icons-material/DeleteOutlineRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopyRounded';
import DownloadIcon from '@mui/icons-material/DownloadRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonthRounded';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightIcon from '@mui/icons-material/ChevronRightRounded';
import AddIcon from '@mui/icons-material/AddRounded';
import CloseIcon from '@mui/icons-material/CloseRounded';
import TodayIcon from '@mui/icons-material/TodayRounded';
import ViewWeekIcon from '@mui/icons-material/ViewWeekRounded';
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonthRounded';
import ListIcon from '@mui/icons-material/ListRounded';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternateRounded';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrashRounded';
import DeleteForeverIcon from '@mui/icons-material/DeleteForeverRounded';

import api from "../../services/api";

// ─── Type config (static — labels are t()-computed below) ─────────────────────
const TYPE_META = {
    linkedin_post: { color: '#0A66C2', bg: 'rgba(10,102,194,0.10)', emoji: '💼' },
    article:       { color: '#FF6B35', bg: 'rgba(255,107,53,0.10)', emoji: '✍️' },
    campaign:      { color: '#701280', bg: 'rgba(112,18,128,0.10)', emoji: '🚀' },
    image:         { color: '#10B981', bg: 'rgba(16,185,129,0.10)', emoji: '🎨' },
    video:         { color: '#EF4444', bg: 'rgba(239,68,68,0.10)', emoji: '🎬' },
    idea:          { color: '#F59E0B', bg: 'rgba(245,158,11,0.10)', emoji: '💡' },
};

function useTypeConfig() {
    const { t } = useTranslation();
    const labels = {
        linkedin_post: t('calendar.type.linkedin_post'),
        article:       t('calendar.type.article'),
        campaign:      t('calendar.type.campaign'),
        image:         t('calendar.type.image'),
        video:         t('calendar.type.video'),
        idea:          t('calendar.type.idea'),
    };
    const result = {};
    Object.entries(TYPE_META).forEach(([k, v]) => {
        result[k] = { ...v, label: labels[k] };
    });
    return result;
}

// ─── Simple Markdown Renderer ─────────────────────────────────────────────────
const MarkdownPreview = ({ content, color }) => {
    const lines = (content || '').split('\n');
    const elements = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        const inlineStyle = (text) => {
            const parts = [];
            let key = 0;
            const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g;
            let last = 0;
            let match;
            while ((match = regex.exec(text)) !== null) {
                if (match.index > last) parts.push(<span key={key++}>{text.slice(last, match.index)}</span>);
                if (match[2]) parts.push(<strong key={key++}>{match[2]}</strong>);
                else if (match[3]) parts.push(<em key={key++}>{match[3]}</em>);
                else if (match[4]) parts.push(<code key={key++} style={{ background: 'rgba(0,0,0,0.07)', borderRadius: 4, padding: '1px 5px', fontFamily: 'monospace', fontSize: '0.88em' }}>{match[4]}</code>);
                last = match.index + match[0].length;
            }
            if (last < text.length) parts.push(<span key={key++}>{text.slice(last)}</span>);
            return parts.length ? parts : text;
        };

        if (/^### (.+)/.test(line)) {
            elements.push(<Typography key={i} sx={{ fontSize: '0.92rem', fontWeight: 700, mt: 1.5, mb: 0.3, color: color || '#1C1C28' }}>{inlineStyle(line.replace(/^### /, ''))}</Typography>);
        } else if (/^## (.+)/.test(line)) {
            elements.push(<Typography key={i} sx={{ fontSize: '1rem', fontWeight: 700, mt: 2, mb: 0.5, color: color || '#1C1C28' }}>{inlineStyle(line.replace(/^## /, ''))}</Typography>);
        } else if (/^# (.+)/.test(line)) {
            elements.push(<Typography key={i} sx={{ fontSize: '1.15rem', fontWeight: 800, mt: 2, mb: 0.5, color: color || '#1C1C28' }}>{inlineStyle(line.replace(/^# /, ''))}</Typography>);
        } else if (/^---+$/.test(line.trim())) {
            elements.push(<Box key={i} sx={{ borderTop: '1px solid rgba(0,0,0,0.1)', my: 1.5 }} />);
        } else if (/^[-*] (.+)/.test(line)) {
            elements.push(
                <Box key={i} sx={{ display: 'flex', gap: 1, ml: 1, mb: 0.3 }}>
                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: color || '#701280', mt: '8px', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.87rem', color: '#333', lineHeight: 1.65 }}>{inlineStyle(line.replace(/^[-*] /, ''))}</Typography>
                </Box>
            );
        } else if (/^\d+\. (.+)/.test(line)) {
            const num = line.match(/^(\d+)\./)[1];
            elements.push(
                <Box key={i} sx={{ display: 'flex', gap: 1, ml: 1, mb: 0.3 }}>
                    <Typography sx={{ fontSize: '0.87rem', color: color || '#701280', fontWeight: 700, minWidth: 18 }}>{num}.</Typography>
                    <Typography sx={{ fontSize: '0.87rem', color: '#333', lineHeight: 1.65 }}>{inlineStyle(line.replace(/^\d+\. /, ''))}</Typography>
                </Box>
            );
        } else if (line.trim() === '') {
            elements.push(<Box key={i} sx={{ height: 6 }} />);
        } else {
            elements.push(<Typography key={i} sx={{ fontSize: '0.87rem', color: '#333', lineHeight: 1.75, mb: 0.2 }}>{inlineStyle(line)}</Typography>);
        }
        i++;
    }
    return <Box sx={{ py: 0.5 }}>{elements}</Box>;
};

// LinkedIn logo SVG
const LinkedInLogo = ({ size = 16, color = '#fff' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
);

function getMonday(d) {
    const date = new Date(d);
    const day = date.getDay();
    const diff = (day === 0 ? -6 : 1 - day);
    date.setDate(date.getDate() + diff);
    date.setHours(0, 0, 0, 0);
    return date;
}

function addDays(d, n) {
    const r = new Date(d);
    r.setDate(r.getDate() + n);
    return r;
}

function toYMD(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseDate(str) {
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d);
}

// ─── Entry Card ───────────────────────────────────────────────────────────────

const EntryCard = ({ entry, onToggle, onDelete, onEdit }) => {
    const { t } = useTranslation();
    const TYPE_CONFIG = useTypeConfig();
    const cfg = TYPE_CONFIG[entry.content_type] || TYPE_CONFIG.idea;
    const isDone = entry.status === 'done';
    return (
        <Box
            sx={{
                position: 'relative',
                borderRadius: '8px',
                overflow: 'hidden',
                background: isDone ? 'rgba(0,0,0,0.02)' : '#fff',
                border: `1px solid ${isDone ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.08)'}`,
                mb: 0.7,
                transition: 'all 0.15s',
                cursor: 'pointer',
                '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)', '& .card-actions': { opacity: 1 } },
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0, top: 0, bottom: 0,
                    width: 3,
                    background: cfg.color,
                    opacity: isDone ? 0.35 : 1,
                },
            }}
            onClick={() => onEdit(entry)}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.8, px: 1.2, py: 0.9, pl: 1.6 }}>
                <Box sx={{ mt: 0.1, cursor: 'pointer', flexShrink: 0 }} onClick={e => { e.stopPropagation(); onToggle(entry); }}>
                    {isDone
                        ? <CheckCircleIcon sx={{ fontSize: '0.95rem', color: '#10B981' }} />
                        : <RadioButtonUncheckedIcon sx={{ fontSize: '0.95rem', color: '#D1D5DB' }} />
                    }
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                        fontSize: '0.78rem', fontWeight: 500, color: isDone ? '#9B9BA8' : '#1C1C28',
                        textDecoration: isDone ? 'line-through' : 'none',
                        lineHeight: 1.4,
                        overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    }}>
                        {cfg.emoji} {entry.title}
                    </Typography>
                    <Box sx={{ mt: 0.4, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                            label={cfg.label}
                            size="small"
                            sx={{ fontSize: '0.6rem', height: 16, background: cfg.bg, color: cfg.color, fontWeight: 600, '& .MuiChip-label': { px: 0.7 } }}
                        />
                        {entry.content_text && (
                            <Chip
                                label={t('calendar.entry_card.text_chip')}
                                size="small"
                                sx={{ fontSize: '0.6rem', height: 16, background: 'rgba(16,185,129,0.1)', color: '#059669', fontWeight: 600, '& .MuiChip-label': { px: 0.7 } }}
                            />
                        )}
                        {entry.image_url && (
                            <Chip
                                label="🖼 Asset"
                                size="small"
                                sx={{ fontSize: '0.6rem', height: 16, background: 'rgba(112,18,128,0.1)', color: '#701280', fontWeight: 600, '& .MuiChip-label': { px: 0.7 } }}
                            />
                        )}
                    </Box>
                </Box>
                <Box className="card-actions" sx={{ opacity: 0, transition: 'opacity 0.15s', display: 'flex', flexDirection: 'column', gap: 0.2, flexShrink: 0 }}>
                    <Tooltip title={t('calendar.entry_card.edit')}>
                        <IconButton size="small" onClick={e => { e.stopPropagation(); onEdit(entry); }} sx={{ p: 0.2, color: '#D1D5DB', '&:hover': { color: '#701280' } }}>
                            <EditIcon sx={{ fontSize: '0.85rem' }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('calendar.entry_card.delete')}>
                        <IconButton size="small" onClick={e => { e.stopPropagation(); onDelete(entry.id); }} sx={{ p: 0.2, color: '#D1D5DB', '&:hover': { color: '#FF6B35' } }}>
                            <DeleteIcon sx={{ fontSize: '0.85rem' }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
        </Box>
    );
};

// ─── Add Entry Modal ──────────────────────────────────────────────────────────

const AddEntryModal = ({ open, onClose, defaultDate, onSaved }) => {
    const { t } = useTranslation();
    const TYPE_CONFIG = useTypeConfig();

    const MODES = [
        { id: 'manual', label: t('calendar.add_modal.mode_manual') },
        { id: 'ai', label: t('calendar.add_modal.mode_ai') },
    ];

    const [mode, setMode] = useState('manual');
    const [title, setTitle] = useState('');
    const [contentType, setContentType] = useState('linkedin_post');
    const [date, setDate] = useState(defaultDate || toYMD(new Date()));
    const [contentText, setContentText] = useState('');
    const [topic, setTopic] = useState('');
    const [generating, setGenerating] = useState(false);
    const [genError, setGenError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setMode('manual');
            setTitle('');
            setContentType('linkedin_post');
            setDate(defaultDate || toYMD(new Date()));
            setContentText('');
            setTopic('');
            setGenError('');
        }
    }, [open, defaultDate]);

    const generate = async () => {
        if (!topic.trim()) return;
        setGenerating(true);
        setGenError('');
        try {
            const resp = await api.post('/api/openai/calendar/generate/', {
                topic: topic.trim(),
                content_type: contentType,
            });
            setTitle(resp.data.title || topic);
            setContentText(resp.data.content_text || '');
        } catch (err) {
            if (err?.response?.status === 402) {
                window.dispatchEvent(new CustomEvent('admind_limit_exceeded', { detail: err.response.data || {} }));
                setGenerating(false);
                return;
            }
            setGenError(err?.response?.data?.error || t('calendar.add_modal.gen_error_default'));
        }
        setGenerating(false);
    };

    const save = async () => {
        if (!title.trim()) return;
        setSaving(true);
        try {
            const res = await api.post('/api/openai/calendar/', {
                title: title.trim(),
                content_type: contentType,
                scheduled_date: date,
                content_text: contentText.trim(),
                status: 'planned',
            });
            onSaved(res.data);
            onClose();
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    const canSave = title.trim() && !saving;

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%,-50%)',
                width: { xs: '93vw', sm: 520 },
                maxHeight: '92vh',
                background: '#fff', borderRadius: '18px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                outline: 'none', overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
            }}>
                <Box sx={{ px: 3, py: 2.2, borderBottom: '1px solid rgba(0,0,0,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.98rem', color: '#1C1C28' }}>{t('calendar.add_modal.title')}</Typography>
                    <IconButton size="small" onClick={onClose} sx={{ color: '#6B6B7B' }}><CloseIcon fontSize="small" /></IconButton>
                </Box>

                <Box sx={{ px: 3, pt: 2.5, flexShrink: 0 }}>
                    <Box sx={{ display: 'flex', background: 'rgba(112,18,128,0.06)', borderRadius: '10px', p: 0.5, border: '1px solid rgba(112,18,128,0.12)' }}>
                        {MODES.map(tab => (
                            <Box
                                key={tab.id}
                                onClick={() => setMode(tab.id)}
                                sx={{
                                    flex: 1, py: 0.9, textAlign: 'center', borderRadius: '8px',
                                    cursor: 'pointer',
                                    background: mode === tab.id ? '#fff' : 'transparent',
                                    boxShadow: mode === tab.id ? '0 1px 4px rgba(0,0,0,0.09)' : 'none',
                                    transition: 'all 0.15s',
                                }}
                            >
                                <Typography sx={{ fontSize: '0.82rem', fontWeight: mode === tab.id ? 700 : 400, color: mode === tab.id ? '#701280' : '#6B6B7B' }}>
                                    {tab.label}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box sx={{ px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', flex: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField
                            select fullWidth label={t('calendar.add_modal.field_type')} value={contentType}
                            onChange={e => setContentType(e.target.value)} size="small"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        >
                            {Object.entries(TYPE_CONFIG).map(([k, v]) => (
                                <MenuItem key={k} value={k}>{v.emoji} {v.label}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            fullWidth label={t('calendar.add_modal.field_date')} type="date" value={date}
                            onChange={e => setDate(e.target.value)} size="small"
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        />
                    </Box>

                    {mode === 'manual' ? (
                        <>
                            <TextField
                                fullWidth label={t('calendar.add_modal.field_title')} value={title}
                                onChange={e => setTitle(e.target.value)} size="small"
                                autoFocus onKeyDown={e => e.key === 'Enter' && !contentText && save()}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                            />
                            <TextField
                                fullWidth label={t('calendar.add_modal.field_content')} value={contentText}
                                onChange={e => setContentText(e.target.value)}
                                multiline rows={5} size="small"
                                placeholder={t('calendar.add_modal.content_placeholder')}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                            />
                        </>
                    ) : (
                        <>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    fullWidth label={t('calendar.add_modal.field_topic')} value={topic}
                                    onChange={e => setTopic(e.target.value)} size="small"
                                    autoFocus
                                    placeholder={t('calendar.add_modal.topic_placeholder')}
                                    onKeyDown={e => e.key === 'Enter' && generate()}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                                />
                                <Button
                                    variant="contained" onClick={generate}
                                    disabled={generating || !topic.trim()}
                                    startIcon={generating ? <CircularProgress size={13} color="inherit" /> : <AutoAwesomeIcon />}
                                    sx={{
                                        flexShrink: 0, py: 0.9, px: 2,
                                        background: 'linear-gradient(135deg, #701280, #8E3A9E)',
                                        borderRadius: '10px', textTransform: 'none', fontWeight: 600,
                                        whiteSpace: 'nowrap', fontSize: '0.82rem',
                                        '&:hover': { background: 'linear-gradient(135deg, #5635CC, #701280)' },
                                    }}
                                >
                                    {generating ? t('calendar.add_modal.generating') : t('calendar.add_modal.generate')}
                                </Button>
                            </Box>

                            {genError && (
                                <Box sx={{ p: 1.5, borderRadius: '10px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)' }}>
                                    <Typography sx={{ fontSize: '0.8rem', color: '#B91C1C' }}>⚠️ {genError}</Typography>
                                </Box>
                            )}

                            {(title || contentText) && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, borderRadius: '12px', background: 'rgba(112,18,128,0.04)', border: '1px solid rgba(112,18,128,0.12)' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <AutoAwesomeIcon sx={{ fontSize: '0.85rem', color: '#701280' }} />
                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#701280' }}>
                                            {t('calendar.add_modal.generated_label')}
                                        </Typography>
                                    </Box>
                                    <TextField
                                        fullWidth label={t('calendar.add_modal.field_title')} value={title}
                                        onChange={e => setTitle(e.target.value)} size="small"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', background: '#fff' } }}
                                    />
                                    <TextField
                                        fullWidth label={t('calendar.add_modal.field_content_generated')} value={contentText}
                                        onChange={e => setContentText(e.target.value)}
                                        multiline rows={6} size="small"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', background: '#fff' } }}
                                    />
                                </Box>
                            )}

                            {generating && !title && !contentText && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, borderRadius: '12px', background: 'rgba(112,18,128,0.04)', border: '1px solid rgba(112,18,128,0.12)' }}>
                                    <CircularProgress size={18} sx={{ color: '#701280' }} />
                                    <Typography sx={{ fontSize: '0.82rem', color: '#701280' }}>
                                        {t('calendar.add_modal.agent_creating')}
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}

                    <Button
                        variant="contained" fullWidth
                        disabled={!canSave}
                        onClick={save}
                        sx={{ background: 'linear-gradient(135deg, #701280, #8E3A9E)', borderRadius: '10px', textTransform: 'none', fontWeight: 600, py: 1.2, mt: 0.5 }}
                    >
                        {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('calendar.add_modal.save_btn')}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

// ─── Edit Entry Modal ─────────────────────────────────────────────────────────

const EditEntryModal = ({ entry, onClose, onSaved, onDeleted }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const TYPE_CONFIG = useTypeConfig();

    const [title, setTitle] = useState(entry?.title || '');
    const [contentType, setContentType] = useState(entry?.content_type || 'linkedin_post');
    const [date, setDate] = useState(entry?.scheduled_date || toYMD(new Date()));
    const [contentText, setContentText] = useState(entry?.content_text || '');
    const [imageUrl, setImageUrl] = useState(entry?.image_url || '');
    const [imagePreview, setImagePreview] = useState(entry?.image_url || '');
    const [status, setStatus] = useState(entry?.status || 'planned');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [copied, setCopied] = useState(false);
    const [contentTab, setContentTab] = useState(0);
    const [linkedInStatus, setLinkedInStatus] = useState(null);
    const [publishing, setPublishing] = useState(false);
    const [snack, setSnack] = useState(null);
    const [uploadingImg, setUploadingImg] = useState(false);
    const [generatingImg, setGeneratingImg] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [confirmDeleteImg, setConfirmDeleteImg] = useState(false);
    const [confirmClose, setConfirmClose] = useState(false);
    const fileInputRef = useRef(null);
    const initialRef = useRef(null);

    useEffect(() => {
        if (entry) {
            setTitle(entry.title || '');
            setContentType(entry.content_type || 'linkedin_post');
            setDate(entry.scheduled_date || toYMD(new Date()));
            setContentText(entry.content_text || '');
            setImageUrl(entry.image_url || '');
            setImagePreview(entry.image_url || '');
            setStatus(entry.status || 'planned');
            setCopied(false);
            setContentTab(0);
            setConfirmDeleteImg(false);
            setConfirmClose(false);
            initialRef.current = {
                title: entry.title || '',
                contentType: entry.content_type || 'linkedin_post',
                date: entry.scheduled_date || toYMD(new Date()),
                contentText: entry.content_text || '',
                imageUrl: entry.image_url || '',
                status: entry.status || 'planned',
            };
        }
    }, [entry]);

    const isDirty = initialRef.current && (
        title !== initialRef.current.title ||
        contentType !== initialRef.current.contentType ||
        date !== initialRef.current.date ||
        contentText !== initialRef.current.contentText ||
        imageUrl !== initialRef.current.imageUrl ||
        status !== initialRef.current.status
    );

    const handleClose = () => {
        if (isDirty) { setConfirmClose(true); } else { onClose(); }
    };

    useEffect(() => {
        api.get('/api/openai/linkedin/status/')
            .then(r => setLinkedInStatus(r.data?.connected === true))
            .catch(() => setLinkedInStatus(false));
    }, []);

    const save = async () => {
        if (!title.trim() || saving) return;
        setSaving(true);
        try {
            const res = await api.patch(`/api/openai/calendar/${entry.id}/`, {
                title: title.trim(),
                content_type: contentType,
                scheduled_date: date,
                content_text: contentText.trim(),
                image_url: imageUrl.trim(),
                status,
            });
            onSaved(res.data);
            onClose();
        } catch (e) { console.error(e); }
        finally { setSaving(false); }
    };

    const handleDelete = async () => {
        if (deleting) return;
        setDeleting(true);
        try {
            await api.delete(`/api/openai/calendar/${entry.id}/`);
            onDeleted(entry.id);
            onClose();
        } catch (e) { console.error(e); }
        finally { setDeleting(false); }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(contentText || title);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handlePublishLinkedIn = async () => {
        if (publishing) return;
        const text = contentText.trim() || title.trim();
        if (!text) { setSnack({ msg: t('calendar.edit_modal.snack_no_content'), severity: 'warning' }); return; }
        setPublishing(true);
        try {
            await api.post('/api/openai/linkedin/publish/', { text });
            setSnack({ msg: t('calendar.edit_modal.snack_linkedin_ok'), severity: 'success' });
            try {
                const res = await api.patch(`/api/openai/calendar/${entry.id}/`, { status: 'done' });
                onSaved(res.data);
            } catch (_) {}
        } catch (e) {
            const msg = e.response?.data?.error || t('calendar.edit_modal.snack_linkedin_err');
            setSnack({ msg, severity: 'error' });
        } finally { setPublishing(false); }
    };

    const handleGenerateImage = async () => {
        if (generatingImg) return;
        const textRef = (contentText || '').trim().slice(0, 400) || title || 'marketing content';
        const prompt = `Create a professional, high-quality marketing image that visually represents the following post content. Style: corporate, clean, modern. No text overlay.\n\nContent: "${textRef}"`;
        setGeneratingImg(true);
        try {
            const genRes = await api.post('/api/openai/dalle-v2/', {
                prompt,
                size: '1792x1024',
                quality: 'high',
            });
            const dataUrl = genRes.data.url;
            setImagePreview(dataUrl);
            setImageUrl(dataUrl);
            setGeneratingImg(false);
            try {
                const fetchResp = await fetch(dataUrl);
                const blob = await fetchResp.blob();
                const formData = new FormData();
                formData.append('image', new File([blob], 'generated.png', { type: 'image/png' }));
                const uploadRes = await api.post('/api/openai/calendar/upload-image/', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                setImageUrl(uploadRes.data.url);
            } catch (_) {}
        } catch {
            setSnack({ msg: t('calendar.edit_modal.snack_img_err'), severity: 'error' });
            setGeneratingImg(false);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const localPreview = URL.createObjectURL(file);
        setImagePreview(localPreview);
        setUploadingImg(true);
        try {
            const formData = new FormData();
            formData.append('image', file);
            const res = await api.post('/api/openai/calendar/upload-image/', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setImageUrl(res.data.url);
            setImagePreview(res.data.url);
        } catch (err) {
            setSnack({ msg: t('calendar.edit_modal.snack_upload_err'), severity: 'error' });
            setImagePreview('');
        } finally {
            setUploadingImg(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const activeCfg = TYPE_CONFIG[contentType] || TYPE_CONFIG.idea;

    return (
        <>
        <Modal open={!!entry} onClose={handleClose}>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%,-50%)',
                width: { xs: '96vw', sm: 640 },
                maxHeight: '94vh',
                background: '#fff', borderRadius: '20px',
                boxShadow: '0 24px 80px rgba(0,0,0,0.18)',
                outline: 'none', overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
            }}>
                <Box sx={{ px: 3, py: 2, borderBottom: '1px solid rgba(0,0,0,0.07)', display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, background: activeCfg.bg }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: activeCfg.color, flexShrink: 0 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: activeCfg.color, flex: 1 }}>
                        {activeCfg.emoji} {title || t('calendar.edit_modal.fallback_title')}
                        {isDirty && <Typography component="span" sx={{ ml: 1, fontSize: '0.7rem', color: '#F59E0B', fontWeight: 500 }}>{t('calendar.edit_modal.unsaved')}</Typography>}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        {contentText && (
                            <Tooltip title={copied ? t('calendar.edit_modal.copied') : t('calendar.edit_modal.copy_content')}>
                                <IconButton size="small" onClick={handleCopy} sx={{ color: activeCfg.color, p: 0.5 }}>
                                    <ContentCopyIcon sx={{ fontSize: '1rem' }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        <IconButton size="small" onClick={handleClose} sx={{ color: '#6B6B7B' }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>

                <Box sx={{ px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', flex: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField
                            select fullWidth label={t('calendar.edit_modal.field_type')} value={contentType}
                            onChange={e => setContentType(e.target.value)} size="small"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        >
                            {Object.entries(TYPE_CONFIG).map(([k, v]) => (
                                <MenuItem key={k} value={k}>{v.emoji} {v.label}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            fullWidth label={t('calendar.edit_modal.field_date')} type="date" value={date}
                            onChange={e => setDate(e.target.value)} size="small"
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        />
                    </Box>

                    <TextField
                        fullWidth label={t('calendar.edit_modal.field_title')} value={title}
                        onChange={e => setTitle(e.target.value)} size="small"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        style={{ display: 'none' }}
                        onChange={handleImageUpload}
                    />

                    {imagePreview ? (
                        <Box sx={{ borderRadius: '14px', border: `1px solid ${activeCfg.color}30` }}>
                            <img
                                src={imagePreview}
                                alt={t('calendar.edit_modal.img_label')}
                                style={{ width: '100%', height: 'auto', display: 'block', cursor: 'pointer', borderRadius: '14px 14px 0 0' }}
                                onClick={() => window.open(imageUrl || imagePreview, '_blank')}
                            />
                            <Box sx={{
                                px: 1.5, py: 0.8,
                                background: activeCfg.bg,
                                borderRadius: '0 0 14px 14px',
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                            }}>
                                <Typography sx={{ fontSize: '0.7rem', color: activeCfg.color, fontWeight: 600, flex: 1 }}>
                                    {uploadingImg ? t('calendar.edit_modal.img_saving') : t('calendar.edit_modal.img_label')}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Tooltip title={t('calendar.edit_modal.img_regenerate')}>
                                        <IconButton size="small" disabled={generatingImg || uploadingImg} onClick={handleGenerateImage} sx={{ p: 0.5, color: activeCfg.color, background: `${activeCfg.color}15`, borderRadius: '6px', '&:hover': { background: `${activeCfg.color}25` } }}>
                                            {generatingImg ? <CircularProgress size={13} sx={{ color: activeCfg.color }} /> : <AutoAwesomeIcon sx={{ fontSize: '0.85rem' }} />}
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('calendar.edit_modal.img_replace')}>
                                        <IconButton size="small" disabled={generatingImg || uploadingImg} onClick={() => fileInputRef.current?.click()} sx={{ p: 0.5, color: activeCfg.color, background: `${activeCfg.color}15`, borderRadius: '6px', '&:hover': { background: `${activeCfg.color}25` } }}>
                                            {uploadingImg ? <CircularProgress size={13} sx={{ color: activeCfg.color }} /> : <AddPhotoAlternateIcon sx={{ fontSize: '0.85rem' }} />}
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('calendar.edit_modal.img_download')}>
                                        <IconButton size="small" onClick={() => { const a = document.createElement('a'); a.href = imagePreview; a.download = 'immagine.png'; a.click(); }} sx={{ p: 0.5, color: activeCfg.color, background: `${activeCfg.color}15`, borderRadius: '6px', '&:hover': { background: `${activeCfg.color}25` } }}>
                                            <DownloadIcon sx={{ fontSize: '0.85rem' }} />
                                        </IconButton>
                                    </Tooltip>
                                    {confirmDeleteImg ? (
                                        <>
                                            <Typography sx={{ fontSize: '0.68rem', color: '#E53935', fontWeight: 600, alignSelf: 'center', px: 0.5 }}>{t('calendar.edit_modal.img_confirm_delete')}</Typography>
                                            <IconButton size="small" onClick={() => { setImageUrl(''); setImagePreview(''); setConfirmDeleteImg(false); }} sx={{ p: 0.5, color: '#fff', background: '#E53935', borderRadius: '6px', '&:hover': { background: '#B71C1C' } }}>
                                                <CheckCircleIcon sx={{ fontSize: '0.85rem' }} />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => setConfirmDeleteImg(false)} sx={{ p: 0.5, color: '#6B6B7B', background: 'rgba(0,0,0,0.08)', borderRadius: '6px' }}>
                                                <CloseIcon sx={{ fontSize: '0.85rem' }} />
                                            </IconButton>
                                        </>
                                    ) : (
                                        <Tooltip title={t('calendar.edit_modal.img_delete')}>
                                            <IconButton size="small" onClick={() => setConfirmDeleteImg(true)} sx={{ p: 0.5, color: '#E53935', background: 'rgba(229,57,53,0.08)', borderRadius: '6px', '&:hover': { background: 'rgba(229,57,53,0.16)' } }}>
                                                <DeleteIcon sx={{ fontSize: '0.85rem' }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{
                            border: generatingImg ? `1.5px dashed ${activeCfg.color}` : '1.5px dashed rgba(0,0,0,0.12)',
                            borderRadius: '14px',
                            py: 1.8, px: 2,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5,
                            background: generatingImg ? activeCfg.bg : 'transparent',
                            transition: 'all 0.2s',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {generatingImg
                                    ? <CircularProgress size={16} sx={{ color: activeCfg.color }} />
                                    : <AddPhotoAlternateIcon sx={{ fontSize: '1.1rem', color: '#C4C4CF' }} />
                                }
                                <Typography sx={{ fontSize: '0.8rem', color: generatingImg ? activeCfg.color : '#9B9BA8', fontWeight: 500 }}>
                                    {generatingImg ? t('calendar.edit_modal.img_generating') : t('calendar.edit_modal.img_none')}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    size="small"
                                    disabled={uploadingImg || generatingImg}
                                    onClick={() => fileInputRef.current?.click()}
                                    startIcon={uploadingImg ? <CircularProgress size={12} color="inherit" /> : <AddPhotoAlternateIcon sx={{ fontSize: '0.9rem !important' }} />}
                                    sx={{
                                        fontSize: '0.75rem', fontWeight: 600, textTransform: 'none',
                                        borderRadius: '8px', px: 1.5, py: 0.6,
                                        border: '1px solid rgba(0,0,0,0.15)', color: '#4A4A5A',
                                        '&:hover': { borderColor: activeCfg.color, color: activeCfg.color, background: activeCfg.bg },
                                    }}
                                >
                                    {uploadingImg ? t('calendar.edit_modal.img_uploading') : t('calendar.edit_modal.img_upload')}
                                </Button>
                                <Button
                                    size="small"
                                    disabled={uploadingImg || generatingImg}
                                    onClick={handleGenerateImage}
                                    startIcon={generatingImg ? <CircularProgress size={12} color="inherit" /> : <AutoAwesomeIcon sx={{ fontSize: '0.9rem !important' }} />}
                                    sx={{
                                        fontSize: '0.75rem', fontWeight: 600, textTransform: 'none',
                                        borderRadius: '8px', px: 1.5, py: 0.6,
                                        background: generatingImg ? 'rgba(112,18,128,0.08)' : 'linear-gradient(135deg, #701280, #8E3A9E)',
                                        color: generatingImg ? activeCfg.color : '#fff',
                                        border: generatingImg ? `1px solid ${activeCfg.color}` : 'none',
                                        '&:hover': { background: 'linear-gradient(135deg, #560D6A, #7A1A90)' },
                                        '&.Mui-disabled': { background: 'rgba(0,0,0,0.08)', color: 'rgba(0,0,0,0.26)' },
                                    }}
                                >
                                    {generatingImg ? t('calendar.edit_modal.img_generating_btn') : t('calendar.edit_modal.img_generate')}
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {contentText !== undefined && (
                        <Box sx={{ border: '1px solid rgba(0,0,0,0.1)', borderRadius: '12px', overflow: 'visible' }}>
                            <Box sx={{ position: 'sticky', top: 0, zIndex: 10, display: 'flex', borderBottom: '1px solid rgba(0,0,0,0.08)', background: '#FAFAF8', borderRadius: '12px 12px 0 0', overflow: 'hidden' }}>
                                {[t('calendar.edit_modal.tab_edit'), t('calendar.edit_modal.tab_preview')].map((label, idx) => (
                                    <Box
                                        key={idx}
                                        onClick={() => setContentTab(idx)}
                                        sx={{
                                            px: 2, py: 0.8, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
                                            color: contentTab === idx ? activeCfg.color : '#9B9BA8',
                                            borderBottom: contentTab === idx ? `2px solid ${activeCfg.color}` : '2px solid transparent',
                                            transition: 'all 0.15s', userSelect: 'none',
                                        }}
                                    >
                                        {label}
                                    </Box>
                                ))}
                            </Box>

                            {contentTab === 0 && (
                                <TextField
                                    fullWidth value={contentText}
                                    onChange={e => setContentText(e.target.value)}
                                    multiline
                                    minRows={8}
                                    maxRows={16}
                                    size="small"
                                    placeholder={t('calendar.edit_modal.content_placeholder')}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 0, fontFamily: 'monospace', fontSize: '0.83rem',
                                            lineHeight: 1.7, border: 'none',
                                            '& fieldset': { border: 'none' },
                                        },
                                        '& textarea': {
                                            overflowY: 'auto !important',
                                            maxHeight: '320px',
                                            '&::-webkit-scrollbar': { width: '4px' },
                                            '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: '4px' },
                                        },
                                    }}
                                />
                            )}

                            {contentTab === 1 && (
                                <Box sx={{
                                    px: 2.5, py: 2,
                                    minHeight: 120, maxHeight: 320,
                                    overflowY: 'auto',
                                    '&::-webkit-scrollbar': { width: '6px' },
                                    '&::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.03)', borderRadius: '4px' },
                                    '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.18)', borderRadius: '4px' },
                                }}>
                                    {contentText ? (
                                        <MarkdownPreview content={contentText} color={activeCfg.color} />
                                    ) : (
                                        <Typography sx={{ color: '#C4C4CF', fontSize: '0.85rem', fontStyle: 'italic' }}>
                                            {t('calendar.edit_modal.no_content_preview')}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {['planned', 'done'].map(s => (
                            <Box
                                key={s}
                                onClick={() => setStatus(s)}
                                sx={{
                                    flex: 1, py: 0.9, textAlign: 'center', borderRadius: '10px',
                                    border: `1px solid ${status === s ? activeCfg.color : 'rgba(0,0,0,0.1)'}`,
                                    background: status === s ? activeCfg.bg : 'transparent',
                                    cursor: 'pointer', transition: 'all 0.15s',
                                }}
                            >
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: status === s ? activeCfg.color : '#9B9BA8' }}>
                                    {s === 'planned' ? t('calendar.edit_modal.status_planned') : t('calendar.edit_modal.status_done')}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    {linkedInStatus === true && (
                        <Box sx={{ background: 'rgba(10,102,194,0.05)', borderRadius: '12px', p: 1.5, border: '1px solid rgba(10,102,194,0.15)' }}>
                            <Typography sx={{ fontSize: '0.73rem', color: '#0A66C2', fontWeight: 600, mb: 1 }}>
                                {t('calendar.edit_modal.linkedin_publish_title')}
                            </Typography>
                            <Button
                                fullWidth
                                disabled={publishing || !contentText.trim()}
                                onClick={handlePublishLinkedIn}
                                startIcon={publishing ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <LinkedInLogo size={16} />}
                                sx={{
                                    background: publishing ? '#aaa' : 'linear-gradient(135deg, #0A66C2, #0077B5)',
                                    color: '#fff', borderRadius: '9px', textTransform: 'none',
                                    fontWeight: 600, fontSize: '0.82rem', py: 0.9,
                                    '&:hover': { background: 'linear-gradient(135deg, #0857A4, #005E94)' },
                                    '&.Mui-disabled': { background: 'rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.26)' },
                                }}
                            >
                                {publishing ? t('calendar.edit_modal.linkedin_publishing') : t('calendar.edit_modal.linkedin_publish_btn')}
                            </Button>
                        </Box>
                    )}
                    {linkedInStatus === false && (
                        <Box
                            onClick={() => { onClose(); navigate('/linkedin'); }}
                            sx={{
                                background: 'rgba(10,102,194,0.04)', borderRadius: '12px', p: 1.5,
                                border: '1px solid rgba(10,102,194,0.18)',
                                display: 'flex', alignItems: 'center', gap: 1.5,
                                cursor: 'pointer',
                                transition: 'background 0.15s, border-color 0.15s',
                                '&:hover': {
                                    background: 'rgba(10,102,194,0.09)',
                                    borderColor: 'rgba(10,102,194,0.35)',
                                },
                            }}
                        >
                            <LinkedInLogo size={18} color="#0A66C2" />
                            <Typography sx={{ fontSize: '0.75rem', color: '#0A66C2', flex: 1, fontWeight: 600 }}>
                                {t('calendar.edit_modal.linkedin_connect')}
                            </Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: 'rgba(10,102,194,0.6)', fontWeight: 500 }}>→</Typography>
                        </Box>
                    )}

                    {!confirmDelete ? (
                        <Box sx={{ display: 'flex', gap: 1, pt: 0.5 }}>
                            <Button
                                variant="outlined" color="error"
                                disabled={deleting}
                                onClick={() => setConfirmDelete(true)}
                                startIcon={<DeleteIcon />}
                                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', py: 1 }}
                            >
                                {t('calendar.edit_modal.delete_btn')}
                            </Button>
                            <Button
                                variant="contained" fullWidth
                                disabled={!title.trim() || saving}
                                onClick={save}
                                sx={{ background: 'linear-gradient(135deg, #701280, #8E3A9E)', borderRadius: '10px', textTransform: 'none', fontWeight: 600, py: 1, '&:hover': { background: 'linear-gradient(135deg, #560D6A, #7A1A90)' } }}
                            >
                                {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('calendar.edit_modal.save_btn')}
                            </Button>
                        </Box>
                    ) : (
                        <Box sx={{ background: '#FFF5F5', border: '1px solid #FFCDD2', borderRadius: '12px', p: 2 }}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#C62828', mb: 0.5 }}>
                                {t('calendar.edit_modal.confirm_delete_title')}
                            </Typography>
                            <Typography sx={{ fontSize: '0.78rem', color: '#6B6B7B', mb: 1.5 }}>
                                {t('calendar.edit_modal.confirm_delete_desc')}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => setConfirmDelete(false)}
                                    sx={{ flex: 1, borderRadius: '9px', textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', borderColor: 'rgba(0,0,0,0.15)', color: '#6B6B7B' }}
                                >
                                    {t('calendar.edit_modal.cancel')}
                                </Button>
                                <Button
                                    variant="contained" color="error"
                                    disabled={deleting}
                                    onClick={handleDelete}
                                    startIcon={deleting ? <CircularProgress size={13} color="inherit" /> : <DeleteIcon />}
                                    sx={{ flex: 1, borderRadius: '9px', textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}
                                >
                                    {deleting ? t('calendar.edit_modal.deleting') : t('calendar.edit_modal.confirm_yes')}
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>
        </Modal>

        <Modal open={confirmClose} onClose={() => setConfirmClose(false)}>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%,-50%)',
                width: { xs: '88vw', sm: 400 },
                background: '#fff', borderRadius: '18px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                outline: 'none', p: 3,
            }}>
                <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#1C1C28', mb: 0.8 }}>
                    {t('calendar.edit_modal.confirm_close_title')}
                </Typography>
                <Typography sx={{ fontSize: '0.87rem', color: '#6B6B7B', mb: 2.5, lineHeight: 1.6 }}>
                    {t('calendar.edit_modal.confirm_close_desc')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        fullWidth variant="outlined"
                        onClick={() => { setConfirmClose(false); onClose(); }}
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, borderColor: 'rgba(0,0,0,0.15)', color: '#6B6B7B' }}
                    >
                        {t('calendar.edit_modal.exit_no_save')}
                    </Button>
                    <Button
                        fullWidth variant="contained"
                        onClick={() => { setConfirmClose(false); save(); }}
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg, #701280, #8E3A9E)', '&:hover': { background: 'linear-gradient(135deg, #560D6A, #7A1A90)' } }}
                    >
                        {t('calendar.edit_modal.save_close')}
                    </Button>
                </Box>
            </Box>
        </Modal>

        <Snackbar
            open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
            <Alert severity={snack?.severity || 'info'} onClose={() => setSnack(null)} sx={{ borderRadius: '12px', fontWeight: 600 }}>
                {snack?.msg}
            </Alert>
        </Snackbar>
        </>
    );
};

// ─── Week View ────────────────────────────────────────────────────────────────

const WeekView = ({ entries, weekStart, onToggle, onDelete, onAdd, onEdit }) => {
    const { t } = useTranslation();
    const DAY_SHORT = t('calendar.days_short', { returnObjects: true });
    const today = toYMD(new Date());
    const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

    const entriesByDay = {};
    entries.forEach(e => {
        const k = e.scheduled_date;
        if (!entriesByDay[k]) entriesByDay[k] = [];
        entriesByDay[k].push(e);
    });

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0, border: '1px solid rgba(0,0,0,0.08)', borderRadius: '14px', overflow: 'hidden', background: '#fff' }}>
            {days.map((day, i) => {
                const ymd = toYMD(day);
                const isToday = ymd === today;
                const dayEntries = (entriesByDay[ymd] || []).sort((a, b) => a.id - b.id);
                return (
                    <Box key={ymd} sx={{ borderRight: i < 6 ? '1px solid rgba(0,0,0,0.07)' : 'none', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{
                            px: 1.5, py: 1.2, textAlign: 'center',
                            background: isToday ? 'rgba(112,18,128,0.07)' : '#FAFAF8',
                            borderBottom: '1px solid rgba(0,0,0,0.07)',
                        }}>
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: isToday ? '#701280' : '#9B9BA8', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                {Array.isArray(DAY_SHORT) ? DAY_SHORT[i] : ['L','M','M','G','V','S','D'][i]}
                            </Typography>
                            <Box sx={{
                                width: 32, height: 32, borderRadius: '50%', mx: 'auto', mt: 0.3,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: isToday ? '#701280' : 'transparent',
                            }}>
                                <Typography sx={{ fontSize: '0.9rem', fontWeight: isToday ? 700 : 500, color: isToday ? '#fff' : '#1C1C28' }}>
                                    {day.getDate()}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ flex: 1, p: 1, minHeight: 120 }}>
                            {dayEntries.map(e => (
                                <EntryCard key={e.id} entry={e} onToggle={onToggle} onDelete={onDelete} onEdit={onEdit} />
                            ))}
                        </Box>

                        <Box sx={{ px: 1, pb: 1 }}>
                            <Box
                                onClick={() => onAdd(ymd)}
                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3, py: 0.5, borderRadius: '6px', cursor: 'pointer', opacity: 0.4, '&:hover': { opacity: 1, background: 'rgba(112,18,128,0.06)' }, transition: 'all 0.15s' }}
                            >
                                <AddIcon sx={{ fontSize: '0.85rem', color: '#701280' }} />
                                <Typography sx={{ fontSize: '0.7rem', color: '#701280', fontWeight: 500 }}>{t('calendar.week_view.add')}</Typography>
                            </Box>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

// ─── Month View ───────────────────────────────────────────────────────────────

const MonthView = ({ entries, year, month, onToggle, onDelete, onAdd, onEdit }) => {
    const { t } = useTranslation();
    const DAY_SHORT = t('calendar.days_short', { returnObjects: true });
    const today = toYMD(new Date());
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const adjustedFirst = (firstDay + 6) % 7;

    const entriesByDay = {};
    entries.forEach(e => {
        const d = parseDate(e.scheduled_date);
        if (d.getFullYear() === year && d.getMonth() === month) {
            const key = d.getDate();
            if (!entriesByDay[key]) entriesByDay[key] = [];
            entriesByDay[key].push(e);
        }
    });

    return (
        <Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
                {(Array.isArray(DAY_SHORT) ? DAY_SHORT : ['L','M','M','G','V','S','D']).map((d, i) => (
                    <Typography key={i} sx={{ textAlign: 'center', fontSize: '0.7rem', fontWeight: 600, color: '#9B9BA8', py: 0.8, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{d}</Typography>
                ))}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '14px', overflow: 'hidden', background: '#fff', gap: 0 }}>
                {Array.from({ length: adjustedFirst }, (_, i) => (
                    <Box key={`e${i}`} sx={{ minHeight: 100, background: '#FAFAF8', borderRight: '1px solid rgba(0,0,0,0.07)', borderBottom: '1px solid rgba(0,0,0,0.07)' }} />
                ))}
                {Array.from({ length: daysInMonth }, (_, i) => {
                    const day = i + 1;
                    const col = (adjustedFirst + i) % 7;
                    const ymd = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const isToday = ymd === today;
                    const dayEntries = entriesByDay[day] || [];
                    return (
                        <Box key={day} sx={{
                            minHeight: 110,
                            borderRight: col < 6 ? '1px solid rgba(0,0,0,0.07)' : 'none',
                            borderBottom: '1px solid rgba(0,0,0,0.07)',
                            p: 0.8,
                            background: isToday ? 'rgba(112,18,128,0.04)' : '#fff',
                            display: 'flex', flexDirection: 'column',
                            overflow: 'hidden',
                            minWidth: 0,
                            cursor: 'pointer',
                            '&:hover .day-add-btn': { opacity: 0.6 },
                            '&:hover': { background: isToday ? 'rgba(112,18,128,0.06)' : 'rgba(0,0,0,0.01)' },
                        }} onClick={() => dayEntries.length === 0 && onAdd(ymd)}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, flexShrink: 0 }}>
                                <Box sx={{
                                    width: 26, height: 26, borderRadius: '50%',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: isToday ? '#701280' : 'transparent',
                                    flexShrink: 0,
                                }}>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: isToday ? 700 : 500, color: isToday ? '#fff' : '#4A4A5A' }}>{day}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {dayEntries.length > 0 && (
                                        <Typography sx={{ fontSize: '0.58rem', color: '#9B9BA8', fontWeight: 500 }}>{dayEntries.length}</Typography>
                                    )}
                                    <Box
                                        className="day-add-btn"
                                        onClick={e => { e.stopPropagation(); onAdd(ymd); }}
                                        sx={{ opacity: 0, transition: 'opacity 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: '50%', background: 'rgba(112,18,128,0.12)', cursor: 'pointer', '&:hover': { background: 'rgba(112,18,128,0.25)', opacity: '1 !important' } }}
                                    >
                                        <AddIcon sx={{ fontSize: '0.65rem', color: '#701280' }} />
                                    </Box>
                                </Box>
                            </Box>
                            <Box sx={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
                                {dayEntries.slice(0, 3).map(e => {
                                    const cfg = TYPE_META[e.content_type] || TYPE_META.idea;
                                    return (
                                        <Box key={e.id} onClick={ev => { ev.stopPropagation(); onEdit(e); }} sx={{
                                            mb: 0.3, px: 0.6, py: 0.25, borderRadius: '4px',
                                            background: cfg.bg, borderLeft: `2px solid ${cfg.color}`,
                                            cursor: 'pointer',
                                            opacity: e.status === 'done' ? 0.5 : 1,
                                            overflow: 'hidden', minWidth: 0,
                                            '&:hover': { filter: 'brightness(0.95)' },
                                        }}>
                                            <Typography sx={{ fontSize: '0.63rem', color: cfg.color, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.4 }}>
                                                {cfg.emoji} {e.title}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                                {dayEntries.length > 3 && (
                                    <Typography sx={{ fontSize: '0.58rem', color: '#701280', pl: 0.5, fontWeight: 500 }}>
                                        {t('calendar.month_view.more', { count: dayEntries.length - 3 })}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

// ─── List View ────────────────────────────────────────────────────────────────

const ListView = ({ entries, onToggle, onDelete, onEdit }) => {
    const { t } = useTranslation();
    const TYPE_CONFIG = useTypeConfig();
    const MONTH_NAMES = t('calendar.months', { returnObjects: true });
    const DAY_SHORT = t('calendar.days_short', { returnObjects: true });
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const sorted = [...entries].sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date));

    if (sorted.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', pt: 6, px: 2 }}>
                <Typography sx={{ fontSize: '2.5rem', mb: 1.5 }}>📅</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1C1C28', mb: 0.5 }}>{t('calendar.list_view.empty_title')}</Typography>
                <Typography sx={{ fontSize: '0.85rem', color: '#6B6B7B', lineHeight: 1.6 }}>
                    {t('calendar.list_view.empty_desc')}
                </Typography>
            </Box>
        );
    }

    const grouped = {};
    sorted.forEach(e => {
        const d = parseDate(e.scheduled_date);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(e);
    });

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {Object.entries(grouped).map(([key, groupEntries]) => {
                const [y, m] = key.split('-').map(Number);
                return (
                    <Box key={key}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#701280', mb: 1.2, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                            {Array.isArray(MONTH_NAMES) ? MONTH_NAMES[m - 1] : m} {y}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7 }}>
                            {groupEntries.map(e => {
                                const cfg = TYPE_CONFIG[e.content_type] || TYPE_CONFIG.idea;
                                const isDone = e.status === 'done';
                                const d = parseDate(e.scheduled_date);
                                return (
                                    <Box key={e.id} onClick={() => onEdit(e)} sx={{ background: '#fff', borderRadius: '12px', border: `1px solid ${isDone ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.08)'}`, display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.4, transition: 'all 0.15s', cursor: 'pointer', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderColor: 'rgba(112,18,128,0.2)' } }}>
                                        <Box sx={{ width: 44, textAlign: 'center', flexShrink: 0 }}>
                                            <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: isDone ? '#D1D5DB' : '#1C1C28', lineHeight: 1 }}>{d.getDate()}</Typography>
                                            <Typography sx={{ fontSize: '0.65rem', color: '#9B9BA8', textTransform: 'uppercase' }}>
                                                {Array.isArray(DAY_SHORT) ? DAY_SHORT[(d.getDay() + 6) % 7] : ''}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ width: 3, height: 36, borderRadius: '2px', background: cfg.color, opacity: isDone ? 0.35 : 1, flexShrink: 0 }} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: isDone ? '#9B9BA8' : '#1C1C28', textDecoration: isDone ? 'line-through' : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {cfg.emoji} {e.title}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                                                <Chip label={cfg.label} size="small" sx={{ fontSize: '0.65rem', height: 18, background: cfg.bg, color: cfg.color, fontWeight: 600 }} />
                                                {e.content_text && (
                                                    <Chip label={t('calendar.list_view.content_chip')} size="small" sx={{ fontSize: '0.65rem', height: 18, background: 'rgba(16,185,129,0.1)', color: '#059669', fontWeight: 600 }} />
                                                )}
                                            </Box>
                                        </Box>
                                        <Tooltip title={isDone ? t('calendar.list_view.mark_planned') : t('calendar.list_view.mark_done')}>
                                            <IconButton size="small" onClick={ev => { ev.stopPropagation(); onToggle(e); }} sx={{ color: isDone ? '#10B981' : '#D1D5DB', p: 0.5 }}>
                                                {isDone ? <CheckCircleIcon sx={{ fontSize: '1.1rem' }} /> : <RadioButtonUncheckedIcon sx={{ fontSize: '1.1rem' }} />}
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('calendar.list_view.edit')}>
                                            <IconButton size="small" onClick={ev => { ev.stopPropagation(); onEdit(e); }} sx={{ color: '#D1D5DB', '&:hover': { color: '#701280' }, p: 0.5 }}>
                                                <EditIcon sx={{ fontSize: '1rem' }} />
                                            </IconButton>
                                        </Tooltip>
                                        {confirmDeleteId === e.id ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={ev => ev.stopPropagation()}>
                                                <Typography sx={{ fontSize: '0.68rem', color: '#E53935', fontWeight: 700, whiteSpace: 'nowrap' }}>{t('calendar.list_view.confirm_delete')}</Typography>
                                                <IconButton size="small" onClick={() => { onDelete(e.id); setConfirmDeleteId(null); }} sx={{ p: 0.4, color: '#fff', background: '#E53935', borderRadius: '6px', '&:hover': { background: '#B71C1C' } }}>
                                                    <CheckCircleIcon sx={{ fontSize: '0.85rem' }} />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => setConfirmDeleteId(null)} sx={{ p: 0.4, color: '#6B6B7B', background: 'rgba(0,0,0,0.08)', borderRadius: '6px' }}>
                                                    <CloseIcon sx={{ fontSize: '0.85rem' }} />
                                                </IconButton>
                                            </Box>
                                        ) : (
                                            <Tooltip title={t('calendar.list_view.delete')}>
                                                <IconButton size="small" onClick={ev => { ev.stopPropagation(); setConfirmDeleteId(e.id); }} sx={{ color: '#D1D5DB', '&:hover': { color: '#FF6B35' }, p: 0.5 }}>
                                                    <DeleteIcon sx={{ fontSize: '1rem' }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

// ─── Trash Panel ──────────────────────────────────────────────────────────────

const TrashPanel = ({ open, onClose, onRestored }) => {
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [acting, setActing] = useState(null);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        api.get('/api/openai/calendar/trash/')
            .then(r => setItems(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [open]);

    const doAction = async (id, action) => {
        setActing(id + action);
        try {
            if (action === 'restore') {
                const res = await api.post('/api/openai/calendar/trash/', { id, action: 'restore' });
                onRestored(res.data);
                setItems(prev => prev.filter(e => e.id !== id));
            } else {
                await api.post('/api/openai/calendar/trash/', { id, action: 'permanent_delete' });
                setItems(prev => prev.filter(e => e.id !== id));
            }
        } catch (e) { console.error(e); }
        finally { setActing(null); }
    };

    const daysLeft = (deletedAt) => {
        const diff = 3 - Math.floor((Date.now() - new Date(deletedAt)) / 86400000);
        return Math.max(0, diff);
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%,-50%)',
                width: { xs: '96vw', sm: 560 }, maxHeight: '80vh',
                background: '#fff', borderRadius: '20px',
                boxShadow: '0 24px 80px rgba(0,0,0,0.16)',
                outline: 'none', overflow: 'hidden', display: 'flex', flexDirection: 'column',
            }}>
                <Box sx={{ px: 3, py: 2, borderBottom: '1px solid rgba(0,0,0,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1C1C28' }}>
                        {t('calendar.trash.title')}
                    </Typography>
                    <IconButton size="small" onClick={onClose} sx={{ color: '#6B6B7B' }}><CloseIcon fontSize="small" /></IconButton>
                </Box>
                <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={24} sx={{ color: '#701280' }} />
                        </Box>
                    ) : items.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 5 }}>
                            <Typography sx={{ fontSize: '2rem', mb: 1 }}>✅</Typography>
                            <Typography sx={{ fontSize: '0.9rem', color: '#9B9BA8' }}>{t('calendar.trash.empty')}</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {items.map(e => {
                                const cfg = TYPE_META[e.content_type] || TYPE_META.idea;
                                const left = daysLeft(e.deleted_at);
                                return (
                                    <Box key={e.id} sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '12px', p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, background: '#FAFAF8' }}>
                                        <Box sx={{ fontSize: '1.2rem', flexShrink: 0 }}>{cfg.emoji}</Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1C1C28', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {e.title}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.7rem', color: '#9B9BA8' }}>
                                                {e.scheduled_date} · {t('calendar.trash.expires', { days: left })}
                                            </Typography>
                                        </Box>
                                        <Tooltip title={t('calendar.trash.restore')}>
                                            <IconButton size="small" disabled={!!acting} onClick={() => doAction(e.id, 'restore')} sx={{ color: '#10B981' }}>
                                                {acting === e.id + 'restore' ? <CircularProgress size={14} /> : <RestoreFromTrashIcon sx={{ fontSize: '1rem' }} />}
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('calendar.trash.delete_forever')}>
                                            <IconButton size="small" disabled={!!acting} onClick={() => doAction(e.id, 'permanent_delete')} sx={{ color: '#E53935' }}>
                                                {acting === e.id + 'permanent_delete' ? <CircularProgress size={14} /> : <DeleteForeverIcon sx={{ fontSize: '1rem' }} />}
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </Box>
        </Modal>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CalendarPage() {
    const { t } = useTranslation();
    const TYPE_CONFIG = useTypeConfig();
    const MONTH_NAMES = t('calendar.months', { returnObjects: true });

    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('week');
    const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
    const [currentMonth, setCurrentMonth] = useState(() => {
        const d = new Date();
        return { year: d.getFullYear(), month: d.getMonth() };
    });
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [addDefaultDate, setAddDefaultDate] = useState('');
    const [editEntry, setEditEntry] = useState(null);
    const [trashOpen, setTrashOpen] = useState(false);

    const fetchEntries = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/openai/calendar/');
            setEntries(res.data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchEntries(); }, [fetchEntries]);

    const toggleStatus = async (entry) => {
        const next = entry.status === 'done' ? 'planned' : 'done';
        try {
            await api.patch(`/api/openai/calendar/${entry.id}/`, { status: next });
            setEntries(prev => prev.map(e => e.id === entry.id ? { ...e, status: next } : e));
        } catch (e) { console.error(e); }
    };

    const deleteEntry = async (id) => {
        try {
            await api.delete(`/api/openai/calendar/${id}/`);
            setEntries(prev => prev.filter(e => e.id !== id));
        } catch (e) { console.error(e); }
    };

    const handleAdd = (defaultDate = '') => {
        setAddDefaultDate(defaultDate);
        setAddModalOpen(true);
    };

    const handleSaved = (newEntry) => {
        setEntries(prev => [...prev, newEntry]);
    };

    const handleEdit = (entry) => setEditEntry(entry);

    const handleEditSaved = (updated) => {
        setEntries(prev => prev.map(e => e.id === updated.id ? updated : e));
    };

    const handleEditDeleted = (id) => {
        setEntries(prev => prev.filter(e => e.id !== id));
    };

    const goToToday = () => {
        setWeekStart(getMonday(new Date()));
        const d = new Date();
        setCurrentMonth({ year: d.getFullYear(), month: d.getMonth() });
    };

    const prevWeek = () => setWeekStart(prev => addDays(prev, -7));
    const nextWeek = () => setWeekStart(prev => addDays(prev, 7));
    const weekEnd = addDays(weekStart, 6);

    const prevMonth = () => setCurrentMonth(prev => {
        const d = new Date(prev.year, prev.month - 1, 1);
        return { year: d.getFullYear(), month: d.getMonth() };
    });
    const nextMonth = () => setCurrentMonth(prev => {
        const d = new Date(prev.year, prev.month + 1, 1);
        return { year: d.getFullYear(), month: d.getMonth() };
    });

    const getMonthName = (idx) => Array.isArray(MONTH_NAMES) ? MONTH_NAMES[idx] : idx;

    const weekLabel = (() => {
        const ws = weekStart;
        const we = weekEnd;
        if (ws.getMonth() === we.getMonth()) {
            return `${ws.getDate()} – ${we.getDate()} ${getMonthName(ws.getMonth())} ${ws.getFullYear()}`;
        }
        return `${ws.getDate()} ${getMonthName(ws.getMonth())} – ${we.getDate()} ${getMonthName(we.getMonth())} ${ws.getFullYear()}`;
    })();

    const btnView = (v, icon, label) => (
        <Button
            size="small"
            startIcon={icon}
            onClick={() => setView(v)}
            sx={{
                fontSize: '0.75rem', textTransform: 'none', fontWeight: view === v ? 700 : 500,
                color: view === v ? '#701280' : '#6B6B7B',
                background: view === v ? 'rgba(112,18,128,0.08)' : 'transparent',
                borderRadius: '8px', px: 1.5, py: 0.6,
                '&:hover': { background: 'rgba(112,18,128,0.06)', color: '#701280' },
            }}
        >
            {label}
        </Button>
    );

    const doneCnt = entries.filter(e => e.status === 'done').length;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#FAFAF8' }}>
            <Box sx={{ px: { xs: 2, md: 3 }, py: 1.8, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 1.5, backgroundColor: '#fff', flexShrink: 0, flexWrap: 'wrap', rowGap: 1 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: 'linear-gradient(135deg, #701280, #FF6B35)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <CalendarMonthIcon sx={{ fontSize: '1.1rem', color: '#fff' }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#1C1C28', lineHeight: 1.2 }}>{t('calendar.title')}</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: '#6B6B7B' }}>
                        {t('calendar.header_subtitle', { count: entries.length, done: doneCnt })}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 0.3, background: '#F5F4FF', borderRadius: '10px', p: 0.4 }}>
                    {btnView('week', <ViewWeekIcon sx={{ fontSize: '0.9rem !important' }} />, t('calendar.view_week'))}
                    {btnView('month', <CalendarViewMonthIcon sx={{ fontSize: '0.9rem !important' }} />, t('calendar.view_month'))}
                    {btnView('list', <ListIcon sx={{ fontSize: '0.9rem !important' }} />, t('calendar.view_list'))}
                </Box>

                <Tooltip title={t('calendar.trash_tooltip')}>
                    <IconButton
                        size="small"
                        onClick={() => setTrashOpen(true)}
                        sx={{ color: '#9B9BA8', '&:hover': { color: '#E53935', background: 'rgba(229,57,53,0.06)' }, borderRadius: '8px', p: 0.8 }}
                    >
                        <DeleteIcon sx={{ fontSize: '1.1rem' }} />
                    </IconButton>
                </Tooltip>
                <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleAdd(toYMD(new Date()))}
                    sx={{ fontSize: '0.8rem', fontWeight: 600, textTransform: 'none', background: 'linear-gradient(135deg, #701280, #8E3A9E)', color: '#fff', borderRadius: '10px', px: 2, py: 0.8, '&:hover': { transform: 'scale(1.02)' }, transition: 'all 0.15s', flexShrink: 0 }}
                >
                    {t('calendar.add')}
                </Button>
            </Box>

            {view !== 'list' && (
                <Box sx={{ px: { xs: 2, md: 3 }, py: 1.2, borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 1.5, backgroundColor: '#fff', flexShrink: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton size="small" onClick={view === 'week' ? prevWeek : prevMonth} sx={{ color: '#6B6B7B', '&:hover': { color: '#701280', background: 'rgba(112,18,128,0.06)' } }}>
                            <ChevronLeftIcon />
                        </IconButton>
                        <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: '#1C1C28', minWidth: 200, textAlign: 'center' }}>
                            {view === 'week' ? weekLabel : `${getMonthName(currentMonth.month)} ${currentMonth.year}`}
                        </Typography>
                        <IconButton size="small" onClick={view === 'week' ? nextWeek : nextMonth} sx={{ color: '#6B6B7B', '&:hover': { color: '#701280', background: 'rgba(112,18,128,0.06)' } }}>
                            <ChevronRightIcon />
                        </IconButton>
                    </Box>
                    <Button
                        size="small"
                        startIcon={<TodayIcon sx={{ fontSize: '0.9rem !important' }} />}
                        onClick={goToToday}
                        sx={{ fontSize: '0.75rem', textTransform: 'none', color: '#701280', fontWeight: 600, borderRadius: '8px', px: 1.5, py: 0.5, '&:hover': { background: 'rgba(112,18,128,0.07)' } }}
                    >
                        {t('calendar.today')}
                    </Button>
                </Box>
            )}

            <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 1.5, md: 2.5 }, '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.1)', borderRadius: 4 } }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
                        <CircularProgress size={28} sx={{ color: '#701280' }} />
                    </Box>
                ) : (
                    <>
                        {view === 'week' && (
                            <WeekView
                                entries={entries}
                                weekStart={weekStart}
                                onToggle={toggleStatus}
                                onDelete={deleteEntry}
                                onAdd={handleAdd}
                                onEdit={handleEdit}
                            />
                        )}
                        {view === 'month' && (
                            <MonthView
                                entries={entries}
                                year={currentMonth.year}
                                month={currentMonth.month}
                                onToggle={toggleStatus}
                                onDelete={deleteEntry}
                                onAdd={handleAdd}
                                onEdit={handleEdit}
                            />
                        )}
                        {view === 'list' && (
                            <>
                                {entries.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', pt: 6, px: 2 }}>
                                        <Box sx={{ fontSize: '3rem', mb: 2 }}>📅</Box>
                                        <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#1C1C28', mb: 0.5 }}>{t('calendar.empty_state.title')}</Typography>
                                        <Typography sx={{ fontSize: '0.875rem', color: '#6B6B7B', lineHeight: 1.6, mb: 3 }}>
                                            {t('calendar.empty_state.desc')}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                                            <Button
                                                variant="contained"
                                                startIcon={<AutoAwesomeIcon />}
                                                component="a"
                                                href="/agent"
                                                sx={{ background: 'linear-gradient(135deg, #701280, #8E3A9E)', borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                                            >
                                                {t('calendar.empty_state.go_agent')}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                startIcon={<AddIcon />}
                                                onClick={() => handleAdd(toYMD(new Date()))}
                                                sx={{ borderColor: '#701280', color: '#701280', borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                                            >
                                                {t('calendar.empty_state.add_manual')}
                                            </Button>
                                        </Box>
                                    </Box>
                                ) : (
                                    <ListView entries={entries} onToggle={toggleStatus} onDelete={deleteEntry} onEdit={handleEdit} />
                                )}
                            </>
                        )}
                    </>
                )}
            </Box>

            {view !== 'list' && (
                <Box sx={{ px: { xs: 2, md: 3 }, py: 1.2, borderTop: '1px solid rgba(0,0,0,0.06)', background: '#fff', display: 'flex', flexWrap: 'wrap', gap: 1.5, flexShrink: 0 }}>
                    {Object.entries(TYPE_CONFIG).map(([k, v]) => (
                        <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '3px', background: v.color }} />
                            <Typography sx={{ fontSize: '0.68rem', color: '#6B6B7B' }}>{v.emoji} {v.label}</Typography>
                        </Box>
                    ))}
                </Box>
            )}

            <AddEntryModal
                open={addModalOpen}
                onClose={() => setAddModalOpen(false)}
                defaultDate={addDefaultDate}
                onSaved={handleSaved}
            />

            <EditEntryModal
                entry={editEntry}
                onClose={() => setEditEntry(null)}
                onSaved={handleEditSaved}
                onDeleted={handleEditDeleted}
            />

            <TrashPanel
                open={trashOpen}
                onClose={() => setTrashOpen(false)}
                onRestored={(entry) => setEntries(prev => {
                    if (prev.find(e => e.id === entry.id)) return prev;
                    return [...prev, entry];
                })}
            />
        </Box>
    );
}
